"use client";

import { useLocation, useNavigate } from "react-router";
import { Facebook, Instagram, Youtube, Mail, ChevronDown, ChevronUp } from "lucide-react";
import { useAddToSubscibers } from "@/apis/newsletter/Mutation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import { useAuthStore } from "@/store/AuthStore";

import {
  HELPWITHORDER,
  RECENTISSUES,
  DELIVERYRELATED,
  PAYEMENTRELATED,
  CANCELLATIONRELATED,
  CONTACTUS,
  TERMS_OF_SERVICE,
  PRIVACY_POLICY,
} from "@/lib/Constants";

// Animation variants
const accordionVariants = {
  open: {
    opacity: 1,
    height: "auto",
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  closed: {
    opacity: 0,
    height: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

const itemVariants = {
  hidden: {
    opacity: 0,
    y: -10
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.2,
      ease: "easeOut"
    }
  }
};

const chevronVariants = {
  rotate: {
    rotate: 180,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  },
  initial: {
    rotate: 0,
    transition: {
      duration: 0.3,
      ease: "easeInOut"
    }
  }
};

const Footer = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const user = useAuthStore((state) => state.user);

  const { mutate, isPending } = useAddToSubscibers();
  const [email, setEmail] = useState("");
  const [openSections, setOpenSections] = useState({
    customerService: false,
    aboutUs: false,
    caremall: false
  });

  const handleSubscribe = () => {
    if (!email.trim()) return alert("Please enter a valid email.");
    mutate({ email });
    setEmail("");
  };

  const toggleSection = (section: string) => {
    setOpenSections(prev => ({
      ...prev,
      [section]: !prev[section as keyof typeof prev]
    }));
  };

  if (
    pathname.includes("/login") ||
    pathname.includes("/forgot") ||
    pathname.includes("/register") ||
    pathname.includes("/otplogin") ||
    (pathname.includes("/contactus") && !user)
  ) {
    return null;
  }

  return (
    <div>
      <footer className="bg-[#262626] text-white py-12 px-4 mt-10">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-8 text-sm">
          {/* Newsletter Section - No changes */}
          <div className="md:col-span-1">
            <motion.div
              className="mb-4"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
            >
              <img
                src="/caremall.png"
                alt="Caremall Logo"
                className="w-[147px] sm:w-28 md:w-36 lg:w-44 xl:w-64 md:h-full m-0 p-0 block md:-ml-4 -ml-2"
                style={{
                  cursor: "pointer",
                }}
                onClick={() => navigate("/")}
              />
            </motion.div>
            <motion.div
              className="flex gap-4 mb-8"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <a
                href="https://www.facebook.com/caremallonline/"
                aria-label="Facebook"
              >
                <Facebook className="text-white size-5 hover:text-blue-400 transition-colors duration-200" />
              </a>
              <a
                href="https://www.instagram.com/caremallindia/"
                aria-label="Instagram"
              >
                <Instagram className="text-white size-5 hover:text-pink-400 transition-colors duration-200" />
              </a>
              <a
                href="https://www.youtube.com/@caremallonline"
                aria-label="YouTube"
              >
                <Youtube className="text-white size-5 hover:text-red-500 transition-colors duration-200" />
              </a>
            </motion.div>
            <motion.h4
              className="text-xl font-bold mb-3"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              {t("newsLetter")}
            </motion.h4>
            <motion.p
              className="text-sm mb-4 text-gray-400 leading-snug"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              {t(
                "subscribeToTheNewsletterAndReceiveINformationaboutPromotionAndNewArrivals"
              )}
            </motion.p>
            <motion.div
              className="flex items-center bg-white rounded-md overflow-hidden w-full max-w-xs border border-gray-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
            >
              <Mail className="text-gray-500 ml-3" size={18} />
              <input
                type="email"
                placeholder={t("enterYourEmailAddresshere")}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="px-3 py-2 w-full text-sm text-black outline-none"
              />
              <button
                onClick={handleSubscribe}
                disabled={isPending}
                className="bg-red-600 text-white px-4 text-sm font-medium h-full py-2 disabled:opacity-60 hover:bg-red-700 transition-colors duration-200"
              >
                {isPending ? "..." : t("subscribe")}
              </button>
            </motion.div>
          </div>
          <div className="flex flex-col gap-3 md:hidden">
            {/* Customer Service Section - Accordion for mobile */}
            <div className="md:col-span-1 md:ml-16 mb-0">
              <motion.div
                className="flex justify-between items-center md:block cursor-pointer md:cursor-auto  md:py-0 border-b md:border-none border-[#757575] "
                onClick={() => toggleSection('customerService')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 }}
                viewport={{ once: true }}
              >
                <h4 className="font-semibold text-lg mb-2 hover:text-red-600 transition-colors duration-200">
                  {t("cusomerServie")}
                </h4>
                <motion.div
                  className="md:hidden"
                  variants={chevronVariants}
                  animate={openSections.customerService ? "rotate" : "initial"}
                >
                  {openSections.customerService ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {(openSections.customerService || window.innerWidth >= 768) && (
                  <motion.div
                    variants={accordionVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    className="overflow-hidden"
                  >
                    <div className="pb-4 md:pb-0 mt-2">
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(RECENTISSUES)}
                      >
                        {t("faq")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.05 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(HELPWITHORDER)}
                      >
                        {t("orders")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.1 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(DELIVERYRELATED)}
                      >
                        {t("deliveries")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.15 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(PAYEMENTRELATED)}
                      >
                        {t("payments")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.2 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(CANCELLATIONRELATED)}
                      >
                        {t("returns_and_refunds")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.25 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/cancel-policy")}
                      >
                        Cancellation policy
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.3 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/shipping-policy")}
                      >
                        Shipping policy
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.35 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(TERMS_OF_SERVICE)}
                      >
                        Terms of Service
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.4 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(PRIVACY_POLICY)}
                      >
                        Privacy Policy
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.45 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate(CONTACTUS)}
                      >
                        {t("Contact Us")}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* About Us Section - Accordion for mobile */}
            <div className="md:col-span-1 mb-0">
              <motion.div
                className="flex justify-between items-center md:block cursor-pointer md:cursor-auto md:py-0 border-b md:border-none border-[#757575] "
                onClick={() => toggleSection('aboutUs')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                viewport={{ once: true }}
              >
                <h4 className="font-semibold mb-0 text-lg hover:text-red-600 transition-colors duration-200">
                  {t("aboutUs")}
                </h4>
                <motion.div
                  className="md:hidden"
                  variants={chevronVariants}
                  animate={openSections.aboutUs ? "rotate" : "initial"}
                >
                  {openSections.aboutUs ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {(openSections.aboutUs || window.innerWidth >= 768) && (
                  <motion.div
                    variants={accordionVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    className="overflow-hidden"
                  >
                    <div className="pb-4 md:pb-0">
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/about-us")}
                      >
                        {t("companies")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.05 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/about-us")}
                      >
                        {t("careers")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.1 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/about-us")}
                      >
                        {t("cities_we_serve")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.15 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/about-us")}
                      >
                        {t("become_caremall_rider")}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Caremall Section - Accordion for mobile */}
            <div className="md:col-span-1 mb-0">
              <motion.div
                className="flex justify-between items-center md:block cursor-pointer md:cursor-auto md:py-0 border-b md:border-none border-[#757575] mb-2"
                onClick={() => toggleSection('caremall')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                viewport={{ once: true }}
              >
                <h4 className="font-semibold text-lg hover:text-red-600 transition-colors duration-200">
                  {t("Caremall")}
                </h4>
                <motion.div
                  className="md:hidden"
                  variants={chevronVariants}
                  animate={openSections.caremall ? "rotate" : "initial"}
                >
                  {openSections.caremall ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </motion.div>
              </motion.div>

              <AnimatePresence>
                {(openSections.caremall || window.innerWidth >= 768) && (
                  <motion.div
                    variants={accordionVariants}
                    initial="closed"
                    animate="open"
                    exit="closed"
                    className="overflow-hidden"
                  >
                    <div className="pb-4 md:pb-0">
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/blogs")}
                      >
                        {t("Blog")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.05 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/blogs")}
                      >
                        {t("news")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.1 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/blogs")}
                      >
                        {t("instant")}
                      </motion.p>
                      <motion.p
                        variants={itemVariants}
                        initial="hidden"
                        animate="visible"
                        transition={{ delay: 0.15 }}
                        className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                        onClick={() => navigate("/blogs")}
                      >
                        {t("daily")}
                      </motion.p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
          {/* Customer Service Section - Accordion for mobile */}
          <div className="md:col-span-1 md:ml-16 mb-0 hidden md:block">
            <motion.div
              className="flex justify-between items-center md:block cursor-pointer md:cursor-auto  md:py-0 border-b md:border-none border-[#757575] "
              onClick={() => toggleSection('customerService')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
            >
              <h4 className="font-semibold text-lg mb-2 hover:text-red-600 transition-colors duration-200">
                {t("cusomerServie")}
              </h4>
              <motion.div
                className="md:hidden"
                variants={chevronVariants}
                animate={openSections.customerService ? "rotate" : "initial"}
              >
                {openSections.customerService ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </motion.div>
            </motion.div>

            <AnimatePresence>
              {(openSections.customerService || window.innerWidth >= 768) && (
                <motion.div
                  variants={accordionVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="overflow-hidden"
                >
                  <div className="pb-4 md:pb-0 mt-2">
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(RECENTISSUES)}
                    >
                      {t("faq")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.05 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(HELPWITHORDER)}
                    >
                      {t("orders")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.1 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(DELIVERYRELATED)}
                    >
                      {t("deliveries")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.15 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(PAYEMENTRELATED)}
                    >
                      {t("payments")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.2 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(CANCELLATIONRELATED)}
                    >
                      {t("returns_and_refunds")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.25 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/cancel-policy")}
                    >
                      Cancellation policy
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.3 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/shipping-policy")}
                    >
                      Shipping policy
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.35 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(TERMS_OF_SERVICE)}
                    >
                      Terms of Service
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.4 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(PRIVACY_POLICY)}
                    >
                      Privacy Policy
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.45 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate(CONTACTUS)}
                    >
                      {t("Contact Us")}
                    </motion.p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* About Us Section - Accordion for mobile */}
          <div className="md:col-span-1 mb-0 hidden md:block">
            <motion.div
              className="flex justify-between items-center md:block cursor-pointer md:cursor-auto md:py-0 border-b md:border-none border-[#757575] "
              onClick={() => toggleSection('aboutUs')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
            >
              <h4 className="font-semibold mb-0 text-lg hover:text-red-600 transition-colors duration-200">
                {t("aboutUs")}
              </h4>
              <motion.div
                className="md:hidden"
                variants={chevronVariants}
                animate={openSections.aboutUs ? "rotate" : "initial"}
              >
                {openSections.aboutUs ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </motion.div>
            </motion.div>

            <AnimatePresence>
              {(openSections.aboutUs || window.innerWidth >= 768) && (
                <motion.div
                  variants={accordionVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="overflow-hidden"
                >
                  <div className="pb-4 md:pb-0">
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/about-us")}
                    >
                      {t("companies")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.05 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/about-us")}
                    >
                      {t("careers")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.1 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/about-us")}
                    >
                      {t("cities_we_serve")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.15 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/about-us")}
                    >
                      {t("become_caremall_rider")}
                    </motion.p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* Caremall Section - Accordion for mobile */}
          <div className="md:col-span-1 mb-0 hidden md:block">
            <motion.div
              className="flex justify-between items-center md:block cursor-pointer md:cursor-auto md:py-0 border-b md:border-none border-[#757575] mb-2"
              onClick={() => toggleSection('caremall')}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
            >
              <h4 className="font-semibold text-lg hover:text-red-600 transition-colors duration-200">
                {t("Caremall")}
              </h4>
              <motion.div
                className="md:hidden"
                variants={chevronVariants}
                animate={openSections.caremall ? "rotate" : "initial"}
              >
                {openSections.caremall ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
              </motion.div>
            </motion.div>

            <AnimatePresence>
              {(openSections.caremall || window.innerWidth >= 768) && (
                <motion.div
                  variants={accordionVariants}
                  initial="closed"
                  animate="open"
                  exit="closed"
                  className="overflow-hidden"
                >
                  <div className="pb-4 md:pb-0">
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/blogs")}
                    >
                      {t("Blog")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.05 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/blogs")}
                    >
                      {t("news")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.1 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/blogs")}
                    >
                      {t("instant")}
                    </motion.p>
                    <motion.p
                      variants={itemVariants}
                      initial="hidden"
                      animate="visible"
                      transition={{ delay: 0.15 }}
                      className="text-gray-400 mb-2 cursor-pointer hover:text-red-600 transition-colors duration-200"
                      onClick={() => navigate("/blogs")}
                    >
                      {t("daily")}
                    </motion.p>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* Footer Bottom - With animations */}
        <motion.div
          className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center md:mt-12 md:pt-8 md:border-t border-gray-700"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          viewport={{ once: true }}
        >
          <p className="text-xs text-gray-400 mb-2 sm:mb-0">{t("copyright")}</p>
          <div className="flex gap-4 text-xs text-gray-400">
            <motion.a
              href="#"
              className="hover:underline"
              onClick={() => navigate("/terms-of-service")}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              {t("termsAndConditions")}
            </motion.a>
            <motion.a
              href="#"
              className="hover:underline"
              onClick={() => navigate("/privacy-policy")}
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            >
              {t("privacyPolicy")}
            </motion.a>
          </div>
        </motion.div>
      </footer>
    </div>
  );
};

export default Footer;